import { UNAUTHORIZED_EVENT } from "api/constants";
import { getErrorMessageForEndpoint } from "helpers/apiErrorMessages/apiErrorMessagesHelper";
import request from "superagent";
import { getTabToken } from "../../common/MetaActions";
import { isTokenExpired } from "../../helpers/auth/token";
import { useRootStore } from "../../store";
import { getDefaultErrorMessage } from "../utils/getDefaultErrorMessage";
import type { SuperAgentRequestWithConfig } from "./types";

/**
 * Plugin that handles API errors and displays appropriate notifications to the user
 *
 * Error Flow:
 * 1. Execute customErrorHandler:
 *    - if it returns true, skip the rest of the flow
 *    - if returns false continue to next checks
 * 2. Checks errorConfig for skipErrorHandling opt-out
 * 3. Errors 403 are handled by redirecting the logged in user
 *    to unauthorized page, if user not logged in, proceed to next check
 * 4. Respects silentOn404 config: suppress notifications for 404 errors
 * 5. Checks for token expiration → sets session expired if needed
 * 6. Checks for abort requests → skips silently if aborted
 * 7. Determines error message with fallback hierarchy:
 *    - customErrorMessage from config
 *    - Mapped message from apiErrorMessages.ts
 *    - Default message based on HTTP method
 * 8. Respects showNotification flag from error message mapping
 * 9. Calls appendErrorNotification to display error
 *
 * Features:
 * - Respects errorConfig for customization (skipErrorHandling, customErrorMessage, silentOn404)
 * - Token expiration detection (sets session expired state)
 * - Abort request detection (skips notification for cancelled requests)
 * - Executes custom error handler if provided
 */
export function errorHandlingPlugin() {
  return function (req: request.SuperAgentRequest) {
    req.on("error", (err: any) => {
      const config = (req as SuperAgentRequestWithConfig).errorConfig;

      const lookupResult = getErrorMessageForEndpoint(
        (req as any).url,
        (req as any).method
      );

      // Execute endpoint-specific custom error handler if available
      if (lookupResult.customErrorHandler) {
        try {
          const requestContext = (req as any).errorHandlerContext;
          const shouldStopFlow = lookupResult.customErrorHandler(
            err,
            requestContext
          );
          if (shouldStopFlow) {
            return;
          }
        } catch (handlerError) {
          console.error("Endpoint custom error handler failed:", handlerError);
        }
      }

      // Allow complete opt-out
      if (config?.skipErrorHandling) {
        return;
      }

      const token = getTabToken();

      // Detects unauthorized responses (401/403) and dispatches
      // a custom event to trigger navigation to the unauthorized page
      // Guards against infinite loops by checking current pathname
      // redirect only logged in user to unauthorized page
      // stop the flow, no need for error notifications
      const status = err?.status || err?.response?.status;
      if (status === 403 && !!token) {
        // Guard against infinite loops - don't redirect if already on unauthorized page
        if (window.location.pathname !== "/unauthorized") {
          // Dispatch custom event for navigation (interceptor is outside React context)
          window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { status } }));
        }
        return;
      }

      // Respect silentOn404 config: suppress notifications for 404 errors
      if (status === 404 && config?.silentOn404) {
        return;
      }

      // Check for token expiration (avoid duplicate error notifications)
      if (token && isTokenExpired(token)) {
        console.warn(
          "Session expired due to expired token — skipping error notification."
        );

        // Set session expired state
        try {
          const userStore = useRootStore.getState().user;
          userStore.setSessionExpired(true);
        } catch (storeError) {
          console.error("Failed to set session expired state:", storeError);
        }

        return;
      }

      // Detect abort requests (user cancelled, network terminated, etc.)
      const errorMessage = typeof err === "string" ? err : err?.message || "";
      const errorName = err?.name?.toLowerCase?.() || "";
      const errorCode = err?.code?.toLowerCase?.() || "";
      const errorStatusCode = err?.statusCode || err?.response?.status;

      const isAbortError =
        errorName === "aborterror" || // fetch abort
        errorCode === "err_canceled" || // axios cancel
        (errorStatusCode === 0 && !errorMessage) || // network aborted
        err?.code === "ABORTED"; // superagent abort

      if (isAbortError) {
        console.warn(
          "Request was aborted or terminated — skipping error notification.",
          err
        );
        return;
      }

      // Determine error message and notification behavior with fallback hierarchy:
      // 1. Explicit customErrorMessage in config (highest priority)
      // 2. Mapped message based on endpoint pattern
      // 3. Default message based on HTTP method (fallback)
      let userFacingErrorMessage: string;
      let shouldShowNotification = true;

      if (config?.customErrorMessage) {
        userFacingErrorMessage = config.customErrorMessage;
        // Note: customErrorMessage doesn't suppress notification
      } else {
        userFacingErrorMessage =
          lookupResult.message ||
          getDefaultErrorMessage((req as any).method, status);
        shouldShowNotification = lookupResult.showNotification;
      }

      // Only notify if configured to do so
      if (shouldShowNotification) {
        try {
          const errorStore = useRootStore.getState().error;
          errorStore.appendErrorNotification(userFacingErrorMessage, err);
        } catch (storeError) {
          console.error("Failed to append error notification:", storeError);
        }
      }
    });

    return req;
  };
}
